import React from "react";
import styled from "styled-components";
import { FaStar, FaVideo, FaComment, FaPhone } from "react-icons/fa";
import { IoCall } from "react-icons/io5";

// Styled Components
const Card = styled.div`
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  max-width: 100%;
  margin: 0 auto;
  font-family: Montserrat;
  font-weight: 400;
  margin-bottom: 1rem;
`;

const TopSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ProfileImage = styled.img`
  width: 6.25rem;
  height: 6.25rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: #2d3748;
  margin: 0;
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #22c55e;
  font-size: 0.9rem;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #22c55e;
`;

const Price = styled.div`
  font-size: 1.3rem;
  font-weight: 400;
  color: #e53e3e;
  align-self: flex-start;
  margin-top: 4px;
`;

const CreatorInfo = styled.div`
  font-size: 1rem;
  color: #4a5565;
  text-transform: capitalize;
  font-weight: 500;
`;

const Tag = styled.span`
  background: #fef2f2;
  color: #e7000b;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 400;
  align-self: flex-start;
  /* margin-right: .75rem ; */
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CallButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #e6f7ff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.9rem;
  color: #1890ff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #edf2f7;
    border-color: #94a3b8;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 400;
  color: #4a5565;
  margin-left: 1.25rem;
`;

const Description = styled.p`
  font-size: .875rem;
  font-weight: 400;
  color: #4a5568;
  line-height: 1.5;
  margin: 0;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 24px;
  /* margin-top: 0.75rem; */
  color: #718096;
  font-size: 0.875rem;
  font-weight: 400;
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  /* margin-top: 20px; */
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #fb2c36;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  transition: background 0.2s;

  &:hover {
    background: #c53030;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #4a5568;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
`;

// Main Component
const SessionCard = ({ session, onBooking }) => {
  // Calculate minimum price from conversation types
  const prices = Object.values(session.conversation_type_amount || {});
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;

  // Format experience years
  const experienceYears = session.creator?.experience_years || 0;
  const formattedExperience =
    experienceYears > 0 ? `${experienceYears}+ years` : "No experience";

  // Format session count
  const sessionCount = session.total_bookings || 0;
  const formattedSessions =
    sessionCount > 0 ? `${sessionCount}+ Completed` : "0 Completed";

  // Determine available call types
  const callTypes = [];
  if (session.conversation_type_amount?.video_call)
    callTypes.push("video_call");
  if (session.conversation_type_amount?.chat) callTypes.push("chat");
  if (session.conversation_type_amount?.call) callTypes.push("call");

  // Rating display
  const hasRating =
    session.average_rating !== null && session.SessionRating?.length > 0;

  return (
    <Card>
      <TopSection>
        <ProfileImage
          src={
            session.creator?.profile_pic?.trim() ||
            "https://via.placeholder.com/80"
          }
          alt={session.creator_name}
        />
        <ContentWrapper>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <TitleSection>
              <Title>{session.name}</Title>
              {/* {session.is_live && (
                <Status>
                  <StatusDot />
                  Available Today
                </Status>
              )} */}
              <CreatorInfo>with {session.creator_name}</CreatorInfo>
            </TitleSection>
            <Price>Starts at ₹{minPrice}</Price>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Tag>{session.category}</Tag>

            <ButtonGroup>
              {callTypes.map((type) => (
                <CallButton key={type}>
                  {type === "video_call" && <FaVideo />}
                  {type === "chat" && <FaComment />}
                  {type === "call" && <IoCall />}
                  {type === "video_call"
                    ? "Video Call"
                    : type === "chat"
                    ? "Chat"
                    : "Voice Call"}
                </CallButton>
              ))}
            </ButtonGroup>
            {hasRating && (
              <RatingSection>
                <FaStar size={24} color="#ffc107" />
                <span>
                  {session.average_rating || 0} ({session.SessionRating.length}+
                  reviews)
                </span>
              </RatingSection>
            )}
          </div>

          <Description>{session.objective}</Description>

          <InfoRow>
            <InfoItem>Experience: {formattedExperience}</InfoItem>
            <InfoItem>
              Languages: {session.language_known || "English"}
            </InfoItem>
            <InfoItem>Sessions: {formattedSessions}</InfoItem>
          </InfoRow>
        </ContentWrapper>
      </TopSection>

      <ActionButtons>
        <PrimaryButton onClick={() => onBooking(session)}>
          Book Session Now
        </PrimaryButton>
        {/* <SecondaryButton>View Full Schedule</SecondaryButton>
        <SecondaryButton>Save for Later</SecondaryButton> */}
      </ActionButtons>
    </Card>
  );
};

// Example usage
export default function SessionResult({ sessions, onBooking }) {
  console.log("Sessions inside SessionResult:", sessions);
  console.log("Sessions type:", typeof sessions);
  console.log("Is sessions array?", Array.isArray(sessions));
  console.log("Sessions length:", sessions?.length);
  
  // Handle undefined, null, or non-array sessions
  if (!sessions) {
    console.log("Sessions is null or undefined");
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#706c6c", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          Start typing to search for sessions...
        </p>
      </div>
    );
  }

  if (!Array.isArray(sessions)) {
    console.error("Sessions is not an array:", sessions);
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#e53935", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          Error: Invalid data format
        </p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div style={{ width: "100%", boxSizing: "border-box" }}>
        <p style={{ color: "#706c6c", fontSize: "1rem", textAlign: "center", padding: "2rem" }}>
          No sessions found. Try a different search term.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        // display: "flex",
        // flexDirection: "column",
        // gap: "1rem",
      }}
    >
      {sessions.map((session, index) => {
        console.log(`Rendering session ${index}:`, session);
        return (
          <SessionCard
            key={session.id || index}
            session={session}
            onBooking={onBooking}
          />
        );
      })}
    </div>
  );
}
